import React from 'react';
import { Card, Flex, Grid, Paper, RingProgress, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { Eyebrow } from './primitives';
import { UserBadge } from '../../../components/user/UserBadge';
import { MyReviewerView } from './types';

interface MeMiniProps {
  name: string;
  position: string;
  grade: string;
  nextGrade: string;
  avatar?: string;
}

export const MeMini: React.FC<MeMiniProps> = ({ name, position, grade, nextGrade, avatar }) => {
  const { t } = useTranslation();
  return (
    <Paper
      p="md"
      radius="md"
      style={{ background: 'var(--mantine-color-default-hover)' }}
      withBorder
    >
      <UserBadge user={{ name, position, avatar }} size={48} />
      <Grid mt="md" gutter={8}>
        <Grid.Col span={6}>
          <Card p="xs" radius="md" withBorder>
            <Eyebrow>{t('perfReview.home.side.current')}</Eyebrow>
            <Text size="sm" fw={600} mt={4}>
              {grade}
            </Text>
          </Card>
        </Grid.Col>
        <Grid.Col span={6}>
          <Card p="xs" radius="md" withBorder>
            <Eyebrow color="teal">{t('perfReview.home.side.goal')}</Eyebrow>
            <Text size="sm" fw={600} mt={4} c="teal">
              {nextGrade}
            </Text>
          </Card>
        </Grid.Col>
      </Grid>
    </Paper>
  );
};

interface AboutRoundProps {
  period: { start: string; end: string; year: string };
  participantsCount: number;
  questionnaire: string;
}

export const AboutRound: React.FC<AboutRoundProps> = ({
  period,
  participantsCount,
  questionnaire,
}) => {
  const { t } = useTranslation();
  const rows = [
    {
      label: t('perfReview.home.side.period'),
      value: `${period.start} — ${period.end} ${period.year}`,
    },
    {
      label: t('perfReview.home.side.participants'),
      value: t('perfReview.home.side.participantsValue', { count: participantsCount }),
    },
    { label: t('perfReview.home.side.questionnaire'), value: questionnaire },
  ].filter((r) => r.value);
  return (
    <Paper withBorder p="md" radius="md">
      <Eyebrow>{t('perfReview.home.side.about')}</Eyebrow>
      <Stack gap="sm" mt="sm">
        {rows.map((r) => (
          <Flex key={r.label} justify="space-between" gap="sm" align="baseline">
            <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
              {r.label}
            </Text>
            <Text
              size="sm"
              fw={500}
              ta="right"
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 0,
              }}
            >
              {r.value}
            </Text>
          </Flex>
        ))}
      </Stack>
    </Paper>
  );
};

// Subject-facing review progress. Deliberately anonymous — shows only HOW MANY
// reviews are done (a percentage), never WHO is reviewing. Peer feedback stays
// blind to the subject; identities live only in the admin summary editor.
export const MyReviewers: React.FC<{ items: MyReviewerView[] }> = ({ items }) => {
  const { t } = useTranslation();
  const total = items.length;
  const submitted = items.filter((r) => r.status === 'submitted').length;
  const pct = total ? Math.round((submitted / total) * 100) : 0;
  const isDone = pct === 100;
  return (
    <Paper withBorder p="md" radius="md">
      <Eyebrow>{t('perfReview.home.side.myReviewers')}</Eyebrow>
      <Flex align="center" justify="center" mt="sm" direction="column" gap={6}>
        <RingProgress
          size={110}
          thickness={10}
          roundCaps
          sections={[
            {
              value: pct,
              color: isDone ? 'teal' : 'var(--mantine-primary-color-filled)',
            },
          ]}
          label={
            <Text ta="center" fw={700} size="lg" style={{ fontVariantNumeric: 'tabular-nums' }}>
              {pct}%
            </Text>
          }
        />
        {/* Процент сам по себе не говорит, от чего он считается. Числа — можно:
            анонимность держится на именах, а не на количестве. */}
        <Text size="xs" c="dimmed" style={{ fontVariantNumeric: 'tabular-nums' }}>
          {t('perfReview.home.side.reviewersDone', { submitted, total })}
        </Text>
      </Flex>
    </Paper>
  );
};
